import { useMemo } from 'react';

import type {
  ProjectCompaniesSort,
  ProjectListing,
} from '@/api/v4/projects.api';
import type { HeaderCellConfig, Row } from '@/ui/table/table.types';
import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';

import { CompanyNameTableCell } from '../components/company-name-table-cell';

interface UseCompanySelectionTableOptions {
  isAllSelected?: boolean;
  isInverted?: boolean;
  onSelectAll: (isChecked: boolean) => void;
  onToggle: (companyId: string) => void;
  projectListings: ProjectListing[] | undefined;
  selectedCompanies: string[];
  totalCount?: number;
}

export const useCompanySelectionTable = ({
  isAllSelected = false,
  isInverted = false,
  onSelectAll,
  onToggle,
  projectListings,
  selectedCompanies,
  totalCount,
}: UseCompanySelectionTableOptions) => {
  const includedCompanies = useMemo(() => {
    if (!projectListings) return [];

    if (isInverted) {
      const excludedSet = new Set(selectedCompanies);
      return projectListings
        .map(l => l.organization.id)
        .filter(id => !excludedSet.has(id));
    }

    return selectedCompanies;
  }, [projectListings, selectedCompanies, isInverted]);

  const visibleListingIds = useMemo(
    () => new Set(projectListings?.map(listing => listing.organization.id)),
    [projectListings],
  );

  const visibleSelectedCount = useMemo(
    () => includedCompanies.filter(id => visibleListingIds.has(id)).length,
    [includedCompanies, visibleListingIds],
  );

  // When all are selected, use totalCount if available, otherwise fall back to loaded count
  const displayCount =
    isAllSelected && totalCount !== undefined
      ? totalCount
      : includedCompanies.length;

  // Determine if header checkbox should show partial selection (minus icon)
  const showPartialSelection =
    !isAllSelected &&
    visibleSelectedCount > 0 &&
    visibleSelectedCount !== (projectListings?.length ?? 0);

  const companiesTableHeaderTemplate = useMemo<
    HeaderCellConfig<ProjectCompaniesSort>[]
  >(
    () => [
      {
        checkboxCount: displayCount,
        checkboxIcon: showPartialSelection ? 'CheckMarkMinus' : undefined,
        columnKey: 'headerCompanyName',
        defaultSortDirection: 'asc',
        id: 'company-table-main-header',
        isChecked: isAllSelected || Boolean(visibleSelectedCount),
        onCheckboxChange: isChecked => {
          if (!projectListings) return;
          onSelectAll(isChecked);
        },
        padding: '30px 0px 20px 30px',
        showCheckbox: true,
        sortKey: 'companyName',
        width: '100%',
      },
    ],
    [
      displayCount,
      isAllSelected,
      onSelectAll,
      projectListings,
      showPartialSelection,
      visibleSelectedCount,
    ],
  );

  const headers = useTranslateHeaders(
    companiesTableHeaderTemplate,
    'projects',
    'enhanceConfigCompanyTable',
  );

  const rows: Row[] = useMemo(() => {
    if (!projectListings) return [];

    return projectListings.map(listing => {
      // When isAllSelected is true, all items are checked
      // Otherwise, check based on selectedCompanies list (with inversion if needed)
      const isChecked =
        isAllSelected ||
        (isInverted
          ? !selectedCompanies.includes(listing.organization.id)
          : selectedCompanies.includes(listing.organization.id));

      const row: Row = {
        cells: [
          {
            breakWord: true,
            maxWidth: '100%',
            value: (
              <CompanyNameTableCell
                listing={listing}
                isChecked={isChecked}
                onToggle={() => onToggle(listing.organization.id)}
              />
            ),
          },
        ],
        id: listing.organization.id,
        rowHeight: '95px',
        rowPadding: {
          left: '30px',
          right: '0px',
        },
      };
      return row;
    });
  }, [isAllSelected, isInverted, onToggle, projectListings, selectedCompanies]);

  return { headers, rows };
};
